import React from 'react';

import { Popover as RadixPopover } from '@radix-ui/themes';
import clsx from 'clsx';

export type PopoverSize = 'small' | 'medium' | 'large';

export interface PopoverRootProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
}

export interface PopoverTriggerProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export interface PopoverContentProps {
  children: React.ReactNode;
  size?: PopoverSize;
  className?: string;
  width?: string | number;
  maxWidth?: string | number;
  minWidth?: string | number;
  height?: string | number;
  maxHeight?: string | number;
  minHeight?: string | number;
  style?: React.CSSProperties;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export interface PopoverCloseProps {
  children: React.ReactNode;
  className?: string;
}

const sizeMapping: Record<PopoverSize, '1' | '2' | '3'> = {
  small: '1',
  medium: '2',
  large: '3',
};

/**
 * PopoverRoot — wrapper for Radix Themes Popover.Root.
 * Contains all the parts of a popover.
 */
export const PopoverRoot: React.FC<PopoverRootProps> = ({
  children,
  open,
  onOpenChange,
  defaultOpen,
}) => (
  <RadixPopover.Root
    open={open}
    onOpenChange={onOpenChange}
    defaultOpen={defaultOpen}
  >
    {children}
  </RadixPopover.Root>
);

/**
 * PopoverTrigger — wrapper for Radix Themes Popover.Trigger.
 * The button that toggles the popover.
 */
export const PopoverTrigger: React.FC<PopoverTriggerProps> = ({
  children,
  className,
  style,
}) => (
  <RadixPopover.Trigger
    className={clsx('popover__trigger', className)}
    style={style}
  >
    {children}
  </RadixPopover.Trigger>
);

/**
 * PopoverContent — wrapper for Radix Themes Popover.Content.
 * The component that pops out when the popover is open.
 */
export const PopoverContent: React.FC<PopoverContentProps> = ({
  children,
  size = 'medium',
  className,
  width,
  maxWidth,
  minWidth,
  height,
  maxHeight,
  minHeight,
  style,
  side = 'bottom',
  ...props
}) => {
  const mappedSize = sizeMapping[size];

  return (
    <RadixPopover.Content
      size={mappedSize}
      className={clsx(
        'popover__content',
        `popover__content--size-${size}`,
        className
      )}
      side={side}
      style={{
        width,
        maxWidth,
        minWidth,
        height,
        maxHeight,
        minHeight,
        ...style,
      }}
      {...props}
    >
      {children}
    </RadixPopover.Content>
  );
};

/**
 * PopoverClose — wrapper for Radix Themes Popover.Close.
 * The button that closes the popover.
 */
export const PopoverClose: React.FC<PopoverCloseProps> = ({
  children,
  className,
}) => (
  <RadixPopover.Close className={clsx('popover__close', className)}>
    {children}
  </RadixPopover.Close>
);

PopoverRoot.displayName = 'PopoverRoot';
PopoverTrigger.displayName = 'PopoverTrigger';
PopoverContent.displayName = 'PopoverContent';
PopoverClose.displayName = 'PopoverClose';
